import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, BookOpen, Clock, Users, Star } from 'lucide-react';
import './DiscoverV2.scss';

export const DiscoverV2: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Featured');
  const [showBackButton, setShowBackButton] = useState(false);

  const categories = [
    'Featured',
    'AI & Data Science',
    'Programming',
    'Design',
    'Business',
    'Marketing',
    'Technology',
    'Creative',
    'Analytics',
    'Strategy',
  ];

  const courses = [
    {
      id: 1,
      title: 'Advanced Machine Learning',
      description:
        'Master deep learning, neural networks, and cutting-edge AI algorithms',
      topic: 'Machine Learning',
      category: 'AI & Data Science',
      estimatedHours: 24,
      lessons: 18,
      enrollments: 2847,
      rating: 4.9,
      status: 'published' as const,
    },
    {
      id: 2,
      title: 'Full Stack Web Development',
      description:
        'Build modern web applications with React, Node.js, and databases',
      topic: 'Web Development',
      category: 'Programming',
      estimatedHours: 36,
      lessons: 24,
      enrollments: 4231,
      rating: 4.8,
      status: 'published' as const,
    },
    {
      id: 3,
      title: 'UI/UX Design Mastery',
      description: 'Create beautiful, user-centered designs and prototypes',
      topic: 'Design Systems',
      category: 'Design',
      estimatedHours: 20,
      lessons: 15,
      enrollments: 1892,
      rating: 4.9,
      status: 'published' as const,
    },
    {
      id: 4,
      title: 'Data Science with Python',
      description:
        'Analyze data, build models, and extract insights using Python',
      topic: 'Data Analysis',
      category: 'Analytics',
      estimatedHours: 28,
      lessons: 21,
      enrollments: 3456,
      rating: 4.7,
      status: 'published' as const,
    },
    {
      id: 5,
      title: 'Digital Marketing Strategy',
      description: 'Master SEO, social media, and content marketing strategies',
      topic: 'Marketing',
      category: 'Business',
      estimatedHours: 16,
      lessons: 12,
      enrollments: 2134,
      rating: 4.6,
      status: 'published' as const,
    },
    {
      id: 6,
      title: 'Blockchain Development',
      description: 'Build decentralized applications and smart contracts',
      topic: 'Blockchain',
      category: 'Technology',
      estimatedHours: 32,
      lessons: 20,
      enrollments: 987,
      rating: 4.8,
      status: 'published' as const,
    },
    {
      id: 7,
      title: 'Creative Writing Workshop',
      description:
        'Develop your storytelling skills and find your unique voice',
      topic: 'Writing',
      category: 'Creative',
      estimatedHours: 12,
      lessons: 10,
      enrollments: 1543,
      rating: 4.5,
      status: 'published' as const,
    },
    {
      id: 8,
      title: 'Business Analytics',
      description:
        'Make data-driven decisions and optimize business performance',
      topic: 'Analytics',
      category: 'Strategy',
      estimatedHours: 18,
      lessons: 14,
      enrollments: 2876,
      rating: 4.7,
      status: 'published' as const,
    },
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setShowBackButton(e.clientY < 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const filteredCourses = courses.filter(course => {
    const matchesSearch =
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === 'Featured' || course.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="discover-v2">
      {/* Back Button */}
      <button
        className={`back-button ${showBackButton ? 'visible' : ''}`}
        onClick={() => navigate('/')}
        aria-label="Go back"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      {/* Header with Logo and Search */}
      <header className="discover-header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-text">Odyssey</span>
          </div>
          <nav className="header-nav">
            <span className="nav-item active">Discover</span>
            <span className="nav-item" onClick={() => navigate('/explore')}>
              Browse
            </span>
          </nav>
        </div>

        <div className="header-center">
          <div className="search-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Try 'data science'"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="header-right">
          <button className="auth-button">Log In</button>
          <button className="auth-button primary">Sign Up</button>
        </div>
      </header>

      {/* Category Tabs */}
      <section className="categories-section">
        <div className="categories-container">
          <div className="categories-tabs">
            {categories.map(category => (
              <button
                key={category}
                className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="main-content">
        <div className="content-container">
          <div className="content-header">
            <h2>Discover Courses</h2>
            <p className="results-count">
              {filteredCourses.length}{' '}
              {filteredCourses.length === 1 ? 'course' : 'courses'} found
            </p>
          </div>

          {/* Courses Grid - Using existing course card structure */}
          <div className="courses-grid">
            {filteredCourses.map(course => (
              <div
                key={course.id}
                className="course-card"
                onClick={() => navigate(`/courses/${course.id}`)}
              >
                <div className="course-thumbnail">
                  <div className="thumbnail-background">
                    <BookOpen className="thumbnail-icon" />
                  </div>
                </div>
                <div className="course-content">
                  <div className="course-header">
                    <h3 className="course-title">
                      {course.title || 'Test Title'}
                    </h3>
                    <span className={`status-badge ${course.status}`}>
                      {course.status}
                    </span>
                  </div>
                  <p className="course-description">
                    {course.description || 'Test description'}
                  </p>
                  <p className="course-topic">
                    Topic: {course.topic || 'Test topic'}
                  </p>
                  <div className="course-meta">
                    <div className="meta-item">
                      <Clock size={16} />
                      <span>{course.estimatedHours}h</span>
                    </div>
                    <div className="meta-item">
                      <BookOpen size={16} />
                      <span>{course.lessons} lessons</span>
                    </div>
                    <div className="meta-item">
                      <Users size={16} />
                      <span>{course.enrollments.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="course-footer">
                    <div className="course-rating">
                      <Star size={14} />
                      <span>{course.rating}</span>
                    </div>
                    <button className="course-action">View Course</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredCourses.length === 0 && (
            <div className="empty-state">
              <BookOpen className="empty-icon" />
              <h3 className="empty-title">No courses found</h3>
              <p className="empty-description">
                Try adjusting your search or selecting a different category.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
